/**
 * Preview Mode Context
 *
 * Admin-only context for Preview-as-User mode.
 * Allows admins to toggle draft content visibility and change civic level
 * without closing open modals (instant re-rendering).
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';

interface PreviewModeContextValue {
  showDraftContent: boolean;
  setShowDraftContent: (show: boolean) => void;
  previewCivicLevel: string | null;
  setPreviewCivicLevel: (level: string | null) => void;
  isPreviewMode: boolean;
  togglePreviewMode: () => void;
}

const PreviewModeContext = createContext<PreviewModeContextValue | null>(null);

interface PreviewModeProviderProps {
  children: ReactNode;
}

export function PreviewModeProvider({ children }: PreviewModeProviderProps) {
  const [showDraftContent, setShowDraftContent] = useState(false);
  const [previewCivicLevel, setPreviewCivicLevel] = useState<string | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);

  const togglePreviewMode = () => {
    setIsPreviewMode(prev => !prev);
    // Reset preview settings when toggling off
    if (isPreviewMode) {
      setShowDraftContent(false);
      setPreviewCivicLevel(null);
    }
  };

  return (
    <PreviewModeContext.Provider
      value={{
        showDraftContent,
        setShowDraftContent,
        previewCivicLevel,
        setPreviewCivicLevel,
        isPreviewMode,
        togglePreviewMode,
      }}
    >
      {children}
    </PreviewModeContext.Provider>
  );
}

export function usePreviewMode() {
  const context = useContext(PreviewModeContext);
  if (!context) {
    throw new Error('usePreviewMode must be used within PreviewModeProvider');
  }
  return context;
}
